export type Sortable = string | number | Date | null | undefined;
type Direction = "ASC" | "DESC";

export function sortBy<T, K extends Sortable>(array: Array<T>, f: (el: T) => K, direction?: Direction): T[] {
  return [...array].sort(newComparator(f, direction));
}

export function keyBy<T, Y = T>(arr: readonly T[], fn: (x: T) => string, valueFn?: (x: T) => Y): Record<string, Y> {
  const result: Record<string, Y> = {};
  arr.forEach((o) => {
    const group = fn(o);
    if (result[group] !== undefined) {
      throw new Error(`${group} already had a value assigned`);
    }
    result[group] = valueFn === undefined ? (o as any as Y) : valueFn(o);
  });
  return result;
}

export function newComparator<T>(f: (x: T) => any, direction?: Direction): (a: T, b: T) => number {
  return (a, b) => {
    const av = f(a);
    const bv = f(b);
    const maybeNegate = direction === "DESC" ? -1 : 1;

    if (!isDefined(av) || !isDefined(bv)) {
      return compareWithUndefined(av, bv) * maybeNegate;
    } else if (typeof av === "number" && typeof bv === "number") {
      return compareNumber(av, bv) * maybeNegate;
    } else if (typeof av === "string" && typeof bv === "string") {
      return compareString(av, bv) * maybeNegate;
    } else if (av instanceof Date && bv instanceof Date) {
      return compareDate(av, bv) * maybeNegate;
    } else {
      throw new Error(`Unsupported sortBy values ${av}, ${bv}`);
    }
  };
}

function compareWithUndefined(av: any, bv: any) {
  if (!av && !bv) {
    return 0;
  } else {
    return !av ? 1 : -1;
  }
}

function compareNumber(av: number, bv: number) {
  return av - bv;
}

function compareString(av: string, bv: string) {
  return av.localeCompare(bv);
}

function compareDate(av: Date, bv: Date) {
  const avDate = av.getTime();
  const bvDate = bv.getTime();

  return avDate - bvDate;
}

export function isDefined<T extends any>(param: T | undefined | null): param is T {
  return param !== null && param !== undefined;
}

// Checks length then creates a clone of arrays and sorts them (with optional sortBy) before doing a strict equality
export function areEquivalentArrays<T>(
  a: ReadonlyArray<T>,
  b: ReadonlyArray<T>,
  sortBy?: ((a: T, b: T) => number) | undefined,
  equalizer?: (a: T, b: T) => boolean,
): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const aSorted = a.slice().sort(sortBy);
  const bSorted = b.slice().sort(sortBy);

  return equalizer ? aSorted.every((x, i) => equalizer(x, bSorted[i])) : aSorted.every((x, i) => x === bSorted[i]);
}

// Compares two arrays and returns the elements that are missing and added from the original to new array
export function compareArrays<T>(originalArr: T[], newArr: T[]): { missing: T[]; added: T[] } {
  // missing = elements that were in the original array but not in the new array
  const missing = originalArr.filter((element) => !newArr.includes(element));
  // added = elements that are in the new array but were not in the original array
  const added = newArr.filter((element) => !originalArr.includes(element));

  return { missing, added };
}
